const dateString = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const addDays = (date, days) => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

export const PAYMENT_MONITORING_CUSTOM_DATE_PRESET = "custom";

export const paymentMonitoringDatePresets = (now = new Date()) => {
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
  const previousMonthStart = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const previousMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

  return [
    { id: "today", text: "Bugün", min: today, max: today },
    {
      id: "last_7_days",
      text: "Son 7 Gün",
      min: addDays(today, -6),
      max: today,
    },
    {
      id: "last_15_days",
      text: "Son 15 Gün",
      min: addDays(today, -14),
      max: today,
    },
    { id: "this_month", text: "Bu Ay", min: monthStart, max: today },
    {
      id: "previous_month",
      text: "Geçen Ay",
      min: previousMonthStart,
      max: previousMonthEnd,
    },
    {
      id: "last_3_months",
      text: "Son 3 Ay",
      min: new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()),
      max: today,
    },
    {
      id: "last_6_months",
      text: "Son 6 Ay",
      min: new Date(now.getFullYear(), now.getMonth() - 6, now.getDate()),
      max: today,
    },
    {
      id: "this_year",
      text: "Bu Yıl",
      min: new Date(now.getFullYear(), 0, 1),
      max: today,
    },
  ].map((preset) => ({
    ...preset,
    min: dateString(preset.min),
    max: dateString(preset.max),
  }));
};

export const defaultPaymentMonitoringFilters = () => {
  const preset = paymentMonitoringDatePresets().find(
    (item) => item.id === "this_month"
  );

  return {
    datePreset: preset.id,
    dateRange: { min: preset.min, max: preset.max },
    paymentProviderIds: [],
    clusterIds: [],
    query: null,
  };
};

const queryValue = (value) => {
  if (Array.isArray(value)) {
    return value.length ? String(value[value.length - 1]) : null;
  }

  return value === null || value === undefined ? null : String(value);
};

const queryIntegerList = (value) => {
  const values = Array.isArray(value) ? value : [value];

  return values
    .reduce((items, item) => items.concat(String(item || "").split(",")), [])
    .map((item) => Number.parseInt(item, 10))
    .filter((item) => Number.isInteger(item) && item > 0);
};

const queryStringList = (value) => {
  const values = Array.isArray(value) ? value : [value];

  return values
    .reduce((items, item) => items.concat(String(item || "").split(",")), [])
    .map((item) => item.trim())
    .filter(Boolean);
};

const queryPositiveInteger = (value, fallback) => {
  const parsed = Number.parseInt(queryValue(value), 10);

  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
};

const queryDate = (value) => {
  const parsed = queryValue(value);

  return parsed && /^\d{4}-\d{2}-\d{2}$/.test(parsed) ? parsed : null;
};

export const paymentMonitoringFiltersFromQuery = (query = {}) => {
  const filters = defaultPaymentMonitoringFilters();
  const presets = paymentMonitoringDatePresets();
  const requestedPreset = queryValue(query.date_preset);
  const isCustomPreset =
    requestedPreset === PAYMENT_MONITORING_CUSTOM_DATE_PRESET;
  const preset =
    presets.find((item) => item.id === requestedPreset) ||
    presets.find((item) => item.id === filters.datePreset);

  return {
    ...filters,
    datePreset: isCustomPreset
      ? PAYMENT_MONITORING_CUSTOM_DATE_PRESET
      : preset.id,
    dateRange: {
      min: queryDate(query["date_range[min]"]) || preset.min,
      max: queryDate(query["date_range[max]"]) || preset.max,
    },
    paymentProviderIds: queryIntegerList(query["payment_provider_ids[]"]),
    clusterIds: queryIntegerList(query["cluster_ids[]"]),
    query: queryValue(query.query)?.trim() || null,
  };
};

export const paymentMonitoringStatusesFromQuery = (query = {}) =>
  queryStringList(query["statuses[]"]);

export const paymentMonitoringOptionsFromQuery = (
  query = {},
  defaults = {}
) => {
  const perPage = queryPositiveInteger(query.per_page, defaults.itemsPerPage);
  const orderBy = queryValue(query.order_by)?.trim();
  const order = queryValue(query.order);

  return {
    ...defaults,
    page: queryPositiveInteger(query.page, defaults.page),
    itemsPerPage: [10, 20, 50].includes(perPage)
      ? perPage
      : defaults.itemsPerPage,
    sortBy: orderBy ? [orderBy] : [...(defaults.sortBy || [])],
    sortDesc:
      order === "asc" || order === "desc"
        ? [order === "desc"]
        : [...(defaults.sortDesc || [])],
  };
};

export const buildPaymentMonitoringUrlQuery = (
  filters,
  options = null,
  extra = {}
) => {
  const query = {
    date_preset: filters.datePreset,
    "date_range[min]": filters.dateRange?.min,
    "date_range[max]": filters.dateRange?.max,
  };

  if (filters.paymentProviderIds?.length) {
    query["payment_provider_ids[]"] = filters.paymentProviderIds.join(",");
  }

  if (filters.clusterIds?.length) {
    query["cluster_ids[]"] = filters.clusterIds.join(",");
  }

  if (filters.statuses?.length) {
    query["statuses[]"] = filters.statuses.join(",");
  }

  if (filters.query?.trim()) {
    query.query = filters.query.trim();
  }

  if (options) {
    query.page = options.page || 1;
    query.per_page = options.itemsPerPage;
    query.order_by = options.sortBy?.[0];
    query.order = options.sortDesc?.[0] ? "desc" : "asc";
  }

  Object.entries(extra).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      query[key] = Array.isArray(value) ? value.join(",") : value;
    }
  });

  return Object.entries(query).reduce((params, [key, value]) => {
    if (value !== null && value !== undefined && value !== "") {
      params[key] = value;
    }

    return params;
  }, {});
};

export const syncPaymentMonitoringUrl = (
  filters,
  options = null,
  extra = {}
) => {
  const params = new URLSearchParams(
    buildPaymentMonitoringUrlQuery(filters, options, extra)
  );
  const search = params.toString();
  const url = `${window.location.pathname}${search ? `?${search}` : ""}${
    window.location.hash
  }`;
  const currentUrl = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  if (url !== currentUrl) {
    window.history.replaceState(window.history.state, "", url);
  }
};

export const buildPaymentMonitoringParams = (filters, extra = {}) => {
  const params = { ...extra };

  if (filters.dateRange?.min) {
    params["date_range[min]"] = filters.dateRange.min;
  }

  if (filters.dateRange?.max) {
    params["date_range[max]"] = filters.dateRange.max;
  }

  if (filters.paymentProviderIds?.length) {
    params.payment_provider_ids = filters.paymentProviderIds;
  }

  if (filters.clusterIds?.length) {
    params.cluster_ids = filters.clusterIds;
  }

  if (filters.statuses?.length) {
    params.statuses = filters.statuses;
  }

  if (filters.query) {
    params.query = filters.query;
  }

  return params;
};

export const formatMonitoringDate = (value) =>
  value ? new Date(value).toLocaleString("tr-TR") : "-";

export const formatMonitoringPercent = (value) =>
  `${(Number(value || 0) * 100).toFixed(1)}%`;
